import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { PRD3050_R01Dto } from './Dto_PRD3050_R01';
import { getCurrentMonthDateRange, getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';
import { formatDateMonth, formatDateYear4 } from 'src/utils/string/stringUtil';

export interface PRD3050_R01Filter {
	compCd: string;
	yearMonth: string;
	dateRange: DateRange | undefined;
	workplaceCd: string;
	jobTeam: string;
	closeTag: string;
	workEmp: string;
	workEmpNm: string;
}

// (소계 제거로 확장 타입 불필요)

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_PRD3050_R01 {
    PRD3050_R01Filter: PRD3050_R01Filter;
    PRD3050_R01PastFilter: PRD3050_R01Filter;
    setPRD3050_R01Filter: (filter: Partial<PRD3050_R01Filter>) => void;
    setPRD3050_R01PastFilter: (filter: Partial<PRD3050_R01Filter>) => void;

    // === 리스트 관련 ===
    PRD3050_R01List: PRD3050_R01Dto[];
    setPRD3050_R01List: (PRD3050_R01List: PRD3050_R01Dto[]) => void;
    insertPRD3050_R01List: (data: PRD3050_R01Dto[], index?: number) => void;
    removePRD3050_R01List: (index: number) => void;
    updatePRD3050_R01List: (data: PRD3050_R01Dto, index: number) => void;

    // === 선택 관련 ===
    selectedPRD3050_R01List: string[];
    setSelectedPRD3050_R01List: (idList: string[]) => void;
    addSelectedPRD3050_R01List: (idList: string[]) => void;
    removeSelectedPRD3050_R01List: (idList?: string[]) => void;
    initSelectedPRD3050_R01List: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;

    // (소계 제거) 추가 상태 없음

    // === 전체 초기화 ===
    initData: () => void;
}

/**
 * 기본 날짜 범위 (현재 월)
 */
const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const defaultPRD3050_R01Filter: PRD3050_R01Filter = {
	compCd: '',
	yearMonth: formatDateYear4(new Date()) + '-' + formatDateMonth(new Date()),
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow()
	},
	workplaceCd: '',
	jobTeam: '',
	closeTag: 'N',
	workEmp: '',
	workEmpNm: '',
};

/**
 * Zustand 스토어 정의
 */
export const usePRD3050_R01Store = create<Store_PRD3050_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_PRD3050_R01, 'PRD3050_R01Filter' | 'PRD3050_R01PastFilter'>) => 
        (filter: Partial<PRD3050_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /** 
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        PRD3050_R01Filter: defaultPRD3050_R01Filter,
        PRD3050_R01PastFilter: defaultPRD3050_R01Filter,
        setPRD3050_R01Filter: createFilterSetter('PRD3050_R01Filter'),
        setPRD3050_R01PastFilter: createFilterSetter('PRD3050_R01PastFilter'),

        // === 리스트 관련 ===
        PRD3050_R01List: [],
        setPRD3050_R01List: (PRD3050_R01List: PRD3050_R01Dto[]) => set({ PRD3050_R01List }),
        insertPRD3050_R01List: (data: PRD3050_R01Dto[], index?: number) => set((state) => {
            const _PRD3050_R01List = [...state.PRD3050_R01List];
            
            if (index !== undefined) {
                _PRD3050_R01List.splice(index, 0, ...data);
            } else {
                _PRD3050_R01List.push(...data);
            }
            return { ...state, PRD3050_R01List: _PRD3050_R01List };
        }),
        removePRD3050_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            PRD3050_R01List: state.PRD3050_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updatePRD3050_R01List: (data: PRD3050_R01Dto, index: number) => set((state) => {
            const _PRD3050_R01List = [...state.PRD3050_R01List];
            _PRD3050_R01List[index] = data;
            return { ...state, PRD3050_R01List: _PRD3050_R01List };
        })
        ,

        // === 선택 관련 ===
        selectedPRD3050_R01List: [],
        setSelectedPRD3050_R01List: (idList: string[]) => set({ selectedPRD3050_R01List: idList }),
        addSelectedPRD3050_R01List: (idList: string[]) => set((state) => {
            const _selectedPRD3050_R01List = [...state.selectedPRD3050_R01List];
            _selectedPRD3050_R01List.push(...idList.filter(value => !_selectedPRD3050_R01List.includes(value)));
            return { ...state, selectedPRD3050_R01List: _selectedPRD3050_R01List };
        }),
        removeSelectedPRD3050_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedPRD3050_R01List: [] };
            }
            
            return {
                ...state,
                selectedPRD3050_R01List: removeItemsFromArray(state.selectedPRD3050_R01List, idList),
            };
        }),
        initSelectedPRD3050_R01List: () => set({ selectedPRD3050_R01List: [] }),
        
        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),

        allCount: 0,
		
        setAllCount: (allCount: number) => set({ allCount }),

        scrollTop: null,
        setScrollTop: (top: number | null) => set({ scrollTop: top }),

        // === 통합 소계 관련 ===
        

        // === 전체 초기화 ===
        initData: () => set(() => ({
            PRD3050_R01Filter: defaultPRD3050_R01Filter,
            PRD3050_R01PastFilter: defaultPRD3050_R01Filter,
            PRD3050_R01List: [],
            selectedPRD3050_R01List: [],
            columnState: null,
            allCount: 0,
            scrollTop: null,
		
            
        })),
    };
});


